import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Zap, Radar, Search, Brain, Compass, Gauge, Trophy, Lock, Eye, EyeOff,
  Sparkles, TrendingUp, AlertTriangle, GraduationCap, Activity, ChevronRight, RefreshCw, History,
} from "lucide-react";
import api from "@/lib/api";
import WhyThis from "@/components/WhyThis";
import FeatureTour from "@/components/FeatureTour";
import Disclaimer from "@/components/Disclaimer";
import TodaysSignal, { nextMoveFrom } from "@/components/TodaysSignal";
import { useCosmetics } from "@/lib/cosmetics";
import { FrameAvatar } from "@/components/cosmetics/FrameAvatar";

const track = (event, data) => api.post("/daily/event", { event, data }).catch(() => {});
const ASSIST = ["GUIDED", "SUPPORTED", "INDEPENDENT"];
const matColor = (s) =>
  s === "TRIGGERED" ? "text-tc-bull bg-tc-bull/10" : s === "NEAR_TRIGGER" ? "text-tc-xp bg-tc-xp/10"
    : s === "INVALIDATED" ? "text-tc-bear bg-tc-bear/10" : "text-tc-sub bg-white/5";

export default function Dashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [watch, setWatch] = useState([]);
  const [signal, setSignal] = useState({});
  const cosmetics = useCosmetics();

  const load = () =>
    Promise.all([
      api.get("/daily"),
      api.get("/training/summary").then((r) => r.data).catch(() => null),
      api.get("/competency/assess/status").then((r) => r.data).catch(() => null),
    ]).then(([r, sum, assess]) => {
      if (r.data.first_run) { navigate("/roadmap"); return; }
      setSignal({ sum, assess });
      setD(r.data);
      if (r.data.authorization?.INTELLIGENCE) api.get("/daily/watch").then((w) => setWatch(w.data.watching || [])).catch(() => {});
    }).catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setAssist = (level) =>
    api.post("/daily/assistance", { level }).then(load).catch(() => {});

  if (!d) return <div className="p-8 text-tc-muted font-mono text-sm" data-testid="daily-loading">Loading TRADECRAFT Daily…</div>;

  const assist = d.assistance.effective;
  const independent = assist === "INDEPENDENT";
  const guided = assist === "GUIDED";
  const brief = d.brief;
  const cards = d.attention.cards || [];
  const na = nextMoveFrom(signal.sum, signal.assess) || d.next_action;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="tradecraft-daily">
      {/* HERO */}
      <div className="flex flex-wrap items-start justify-between gap-3 animate-fade-up">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-tc-coach">
            <Activity className="w-3.5 h-3.5" /> TRADECRAFT DAILY · Market Intelligence • Your Edge • Next Action
          </div>
          <div className="flex items-center gap-3 mt-1">
            {cosmetics?.equipped_frame && <FrameAvatar name={d.name} frame={cosmetics.equipped_frame} size={46} testid="dashboard-frame-avatar" />}
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="daily-greeting">
              Good {timeOfDay()}, <span className="text-tc-coach">{d.name}</span>
            </h1>
          </div>
        </div>
        {/* Assistance toggle — presentation only */}
        <div className="flex flex-col items-end gap-1" data-testid="assistance-toggle">
          <div className="inline-flex rounded-lg border border-white/12 overflow-hidden">
            {ASSIST.map((lvl) => (
              <button key={lvl} data-testid={`assist-${lvl.toLowerCase()}`} onClick={() => setAssist(lvl === assist ? null : lvl)}
                className={`px-3 py-1.5 text-[11px] font-mono tracking-wide transition-colors ${lvl === assist ? "bg-tc-coach text-tc-bg" : "text-tc-sub hover:text-tc-txt"}`}>
                {lvl}
              </button>
            ))}
          </div>
          <span className="text-[10px] text-tc-muted flex items-center gap-1">
            {d.assistance.override ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            Assistance = explanation depth only · not access
          </span>
        </div>
      </div>

      {/* TODAY'S SIGNAL */}
      <TodaysSignal d={d} sum={signal.sum} assess={signal.assess} />

      {/* YOUR NEXT MOVE */}
      {na && (
        <div className="tc-card p-5 border-tc-coach/40 bg-gradient-to-br from-tc-coach/15 to-transparent flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-up" data-testid="next-move">
          <div className="flex-1">
            <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Your next move</div>
            <div className="font-heading text-xl sm:text-2xl font-bold mt-1" data-testid="next-move-label">{na.label}</div>
            {!independent && <p className="text-tc-sub text-sm mt-1 max-w-xl">{na.why}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="next-move-btn" onClick={() => { track("next_action_opened", { kind: na.kind }); navigate(na.to); }}
              className="rounded-xl bg-tc-coach text-tc-bg font-bold px-6 py-3 hover:bg-tc-coach/90 transition-colors flex items-center gap-2">
              {na.label} <ArrowRight className="w-4 h-4" />
            </button>
            <button data-testid="roadmap-btn" onClick={() => { track("roadmap_opened"); navigate("/roadmap"); }}
              className="rounded-xl border border-white/15 text-tc-sub px-4 py-3 hover:border-tc-coach/40 hover:text-tc-coach transition-colors flex items-center gap-2">
              <Compass className="w-4 h-4" /> {guided ? "See my path" : "Roadmap"}
            </button>
          </div>
        </div>
      )}

      {/* RESUME WHERE YOU LEFT OFF */}
      {d.resume && (
        independent ? (
          <button data-testid="resume-card" onClick={() => { track("resume_opened", { kind: d.resume.kind, gated: d.resume.gated }); navigate(d.resume.cta.to); }}
            className="w-full tc-card px-4 py-2.5 flex items-center gap-3 border-white/10 hover:border-tc-coach/40 transition-colors text-left">
            <span className="text-[10px] font-mono tracking-widest text-tc-coach">RESUME ▸</span>
            <span className="font-mono text-sm text-tc-txt flex-1 truncate">{d.resume.activity}</span>
            {d.resume.status && d.resume.status !== "CURRENT" && (
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-tc-bear/10 text-tc-bear">{d.resume.status}</span>
            )}
            <ArrowRight className="w-4 h-4 text-tc-coach shrink-0" />
          </button>
        ) : (
          <div data-testid="resume-card" className="tc-card p-4 border-tc-coach/25 bg-gradient-to-br from-tc-coach/8 to-transparent flex flex-col sm:flex-row sm:items-center gap-3 animate-fade-up">
            <div className="flex-1">
              <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" /> {guided ? "Continue where you left off" : "Resume"}
              </div>
              <div className="font-medium text-tc-txt mt-1 flex items-center gap-2">
                {d.resume.activity}
                {d.resume.status && d.resume.status !== "CURRENT" && (
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-tc-bear/10 text-tc-bear">{d.resume.status}</span>
                )}
              </div>
              {d.resume.note && !independent && <p className="text-[12px] text-tc-muted mt-0.5">{d.resume.note}</p>}
            </div>
            <button data-testid="resume-cta" onClick={() => { track("resume_opened", { kind: d.resume.kind, gated: d.resume.gated }); navigate(d.resume.cta.to); }}
              className="shrink-0 rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-2 text-sm flex items-center gap-2">
              {d.resume.cta.label} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )
      )}

      {/* MARKET BRIEF (deterministic) — Intelligence unlocked only */}
      {d.authorization?.INTELLIGENCE && (brief.available ? (
        <div className="tc-card p-4" data-testid="market-brief">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-sm">
            <Metric n={brief.screened} label="screened" />
            <Metric n={brief.surfaced} label="deserve attention" accent="text-tc-coach" />
            <Metric n={brief.materially_changed} label="materially changed" accent="text-tc-xp" />
            <span className="text-tc-muted">·</span>
            <span className="text-tc-sub">{brief.environment}</span>
          </div>
          {!independent && (
            <div className="grid sm:grid-cols-3 gap-3 mt-3 text-sm">
              {brief.most_developed && <BriefCell label="Most developed" value={`${brief.most_developed.symbol} · ${brief.most_developed.situation_type}`} sub={brief.most_developed.maturity} />}
              {brief.most_important_conflict && <BriefCell label="Key conflict" value={brief.most_important_conflict.symbol} sub={brief.most_important_conflict.conflict} />}
            </div>
          )}
        </div>
      ) : (
        <div className="tc-card p-4 text-tc-muted text-sm" data-testid="market-brief">{brief.note}</div>
      ))}

      {/* WHAT DESERVES MY ATTENTION */}
      <section data-testid="attention-section">
        <SectionHead icon={Radar} title="What deserves attention" right={d.attention.authorized ? (
          <button data-testid="open-intelligence-btn" onClick={() => { track("next_action_opened", { kind: "intelligence" }); navigate("/intelligence"); }}
            className="text-[12px] text-tc-coach hover:text-tc-coach/80 flex items-center gap-1">Open Intelligence <ChevronRight className="w-3.5 h-3.5" /></button>
        ) : null} />

        {guided && !d.tours_seen?.includes("attention") && (
          <div className="mb-3">
            <FeatureTour feature="attention" title="Attention Queue" seen={d.tours_seen}
              what="The few current markets TRADECRAFT thinks are worth a look right now."
              why="It scans many markets so you can focus your judgment on the ones that matter."
              how="Open any card's 'Why am I seeing this?' to see the evidence — then decide for yourself." />
          </div>
        )}

        {!d.attention.authorized ? (
          <div className="tc-card p-5 border-white/10" data-testid="attention-locked">
            <div className="flex items-center gap-2 text-tc-sub"><Lock className="w-4 h-4 text-tc-mastery" />
              <span className="text-sm">Intelligence unlocks once you've earned it. {d.attention.reason_locked}</span>
            </div>
            <p className="text-tc-muted text-[12px] mt-2">You can still learn from live markets below — same professional engine, guided for where you are.</p>
          </div>
        ) : cards.length === 0 ? (
          <div className="tc-card p-5 text-tc-muted text-sm">No situations currently surfaced. That's normal — quality over noise.</div>
        ) : independent ? (
          <div className="tc-card overflow-hidden" data-testid="attention-table">
            {cards.map((c) => (
              <div key={c.candidate_id} className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                <span className="font-mono text-sm text-tc-txt w-24">{c.symbol}</span>
                <span className="text-[12px] text-tc-sub flex-1 truncate">{c.situation_type}</span>
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${matColor((c.condition_maturity || {}).state)}`}>{(c.condition_maturity || {}).state}</span>
                <span className="font-mono text-sm text-tc-coach w-14 text-right">AP {Math.round((c.attention_priority || {}).total || 0)}</span>
                <button data-testid={`row-analyze-${c.symbol}`} onClick={() => { track("candidate_analyzed", { symbol: c.symbol }); navigate(`/analyst?asset=${encodeURIComponent(c.symbol)}`); }}
                  className="text-tc-coach hover:text-tc-coach/80"><Search className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="attention-cards">
            {cards.slice(0, guided ? 3 : 6).map((c) => (
              <div key={c.candidate_id} className="tc-card p-4" data-testid={`attention-card-${c.symbol}`}>
                <div className="flex items-center justify-between">
                  <div className="font-heading font-bold text-lg">{c.symbol}</div>
                  <span className="font-mono text-sm text-tc-coach">AP {Math.round((c.attention_priority || {}).total || 0)}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[12px] text-tc-sub">{c.situation_type}</span>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${matColor((c.condition_maturity || {}).state)}`}>{(c.condition_maturity || {}).state}</span>
                  {c.why_you && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-tc-mastery/10 text-tc-mastery">FOR YOU</span>}
                </div>
                <WhyThis card={c} assist={assist} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* SINCE YOUR LAST VISIT */}
      {brief.available && (
        <section data-testid="since-last-visit">
          <SectionHead icon={RefreshCw} title={d.changes.first_visit ? "What TRADECRAFT is tracking" : "Since your last visit"} />
          <div className="tc-card p-4">
            {d.changes.items.length === 0 ? (
              <div className="text-tc-muted text-sm">No material structural changes. Price moved, but structure held.</div>
            ) : (
              <ul className="space-y-2">
                {d.changes.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm" data-testid={`change-${it.symbol}`}>
                    <span className={`mt-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 ${it.type === "INVALIDATED" ? "bg-tc-bear/10 text-tc-bear" : it.type === "NEW" ? "bg-tc-coach/10 text-tc-coach" : "bg-tc-xp/10 text-tc-xp"}`}>{it.type}</span>
                    <div>
                      <span className="font-mono text-tc-txt">{it.symbol}</span>
                      <span className="text-tc-sub"> — {it.text}</span>
                      {it.detail?.length > 0 && !independent && <div className="text-[12px] text-tc-muted mt-0.5">{it.detail.join(" · ")}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {d.changes.unchanged > 0 && <div className="text-[11px] text-tc-muted mt-2">{d.changes.unchanged} other{d.changes.unchanged > 1 ? "s" : ""} unchanged (price moved, structure held).</div>}
          </div>
        </section>
      )}

      {/* LIVE MARKET -> LEARNING */}
      {d.live_learning && (
        <div className="tc-card p-5 border-tc-mastery/30 bg-gradient-to-br from-tc-mastery/10 to-transparent" data-testid="live-learning">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-mastery flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Live market → your development</div>
          <p className="text-tc-txt mt-1 font-medium">{d.live_learning.headline}</p>
          {!independent && d.live_learning.conflicting_evidence?.length > 0 && (
            <p className="text-tc-sub text-sm mt-1">The interesting part is the conflict: {d.live_learning.conflicting_evidence[0]}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            <button data-testid="live-learning-chart" onClick={() => { track("live_learning_started", { symbol: d.live_learning.symbol }); navigate(`/co-analysis?asset=${encodeURIComponent(d.live_learning.symbol)}`); }}
              className="rounded-lg bg-tc-mastery text-tc-bg font-semibold px-4 py-2 text-sm flex items-center gap-2"><Brain className="w-4 h-4" /> See it on the chart</button>
            <button data-testid="live-learning-practice" onClick={() => { track("live_learning_started", { domain: d.live_learning.domain }); navigate(d.live_learning.practice_route); }}
              className="rounded-lg border border-white/15 text-tc-sub px-4 py-2 text-sm hover:text-tc-txt flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Practice {d.live_learning.domain}</button>
          </div>
        </div>
      )}

      {/* TODAY'S EDGE + WATCH */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="tc-card p-5" data-testid="todays-edge">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Today's Edge</div>
          <div className="mt-3 space-y-2 text-sm">
            <EdgeRow label="Current strength" value={d.todays_edge.strength} accent="text-tc-bull" />
            <EdgeRow label="Developing" value={d.todays_edge.developing} accent="text-tc-xp" />
            <EdgeRow label="Watch for" value={d.todays_edge.watch_for} icon={AlertTriangle} />
            {d.todays_edge.suggested_practice && <EdgeRow label="Suggested practice" value={`${d.todays_edge.suggested_practice.symbol} · ${d.todays_edge.suggested_practice.situation_type}`} accent="text-tc-coach" />}
            {d.todays_edge.next_milestone && (
              <div className="mt-2 pt-2 border-t border-white/5 text-[12px] text-tc-muted">
                Next capability: <span className="text-tc-mastery font-mono">{d.todays_edge.next_milestone.capability}</span> — {d.todays_edge.next_milestone.reason || "demonstrate more competency"}
              </div>
            )}
          </div>
        </div>

        <div className="tc-card p-5" data-testid="watch-panel">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Watching</div>
            {d.attention.authorized && <button data-testid="watch-open-intel" onClick={() => navigate("/intelligence")} className="text-[12px] text-tc-coach">Manage</button>}
          </div>
          {!d.attention.authorized ? (
            <p className="text-tc-muted text-sm mt-3">Watch keeps a situation visible as its structure evolves — available once Intelligence unlocks.</p>
          ) : watch.length === 0 ? (
            <p className="text-tc-muted text-sm mt-3">Nothing on your watchlist yet. Open a candidate in Intelligence and choose Watch to keep it visible here.</p>
          ) : (
            <ul className="mt-3 space-y-2.5">
              {watch.map((w) => (
                <li key={w.symbol} className="text-sm" data-testid={`watch-${w.symbol}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-tc-txt">{w.symbol}</span>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${w.invalidated ? "bg-tc-bear/10 text-tc-bear" : matColor((w.maturity || {}).state)}`}>
                      {w.invalidated ? "INVALIDATED" : (w.maturity || {}).state || "—"}
                    </span>
                  </div>
                  {w.changes_since_watch?.length > 0 && <div className="text-[12px] text-tc-sub mt-0.5">{w.changes_since_watch.map((c) => c.text || c).slice(0, 2).join(" · ")}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* WHERE AM I — competency snapshot */}
      <div className="tc-card p-5" data-testid="competency-snapshot">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-sub flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> Where you are</div>
          <button data-testid="progress-link" onClick={() => navigate("/skills")} className="text-[12px] text-tc-coach flex items-center gap-1">Progress <ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {d.competency.domains.map((dm) => (
            <div key={dm.domain} className="flex items-center justify-between text-[12px] bg-white/[0.02] rounded-md px-2.5 py-1.5">
              <span className="text-tc-sub truncate mr-2">{dm.domain}</span>
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${dm.level === "MASTERED" ? "text-tc-bull bg-tc-bull/10" : dm.level === "PROFICIENT" ? "text-tc-coach bg-tc-coach/10" : dm.level === "DEVELOPING" ? "text-tc-xp bg-tc-xp/10" : "text-tc-muted bg-white/5"}`}>{dm.level.replace(/_/g, " ")}</span>
            </div>
          ))}
        </div>
      </div>

      <Disclaimer />
    </div>
  );
}

function timeOfDay() {
  const h = new Date().getHours();
  return h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
}
function Metric({ n, label, accent = "text-tc-txt" }) {
  return <span className="flex items-baseline gap-1.5"><span className={`text-lg font-semibold ${accent}`}>{n}</span><span className="text-tc-muted text-[12px]">{label}</span></span>;
}
function BriefCell({ label, value, sub, accent }) {
  return (
    <div className={`rounded-lg px-3 py-2 ${accent ? "bg-tc-mastery/10 border border-tc-mastery/25" : "bg-white/[0.02]"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted">{label}</div>
      <div className={`font-medium mt-0.5 ${accent ? "text-tc-mastery" : "text-tc-txt"}`}>{value}</div>
      {sub && <div className="text-[12px] text-tc-sub truncate">{sub}</div>}
    </div>
  );
}
function SectionHead({ icon: Icon, title, right }) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Icon className="w-4.5 h-4.5 text-tc-coach" /> {title}</h2>
      {right}
    </div>
  );
}
function EdgeRow({ label, value, accent = "text-tc-txt", icon: Icon }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-tc-sub flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5 text-tc-xp" />}{label}</span>
      <span className={`font-medium ${accent}`}>{value || "—"}</span>
    </div>
  );
}
